import type { FilterRule, MobileCardConfiguration } from '../../types/list';

/**
 * Shared constants and value mappings for transaction summary lists
 */

/**
 * Zones mobile card for transaction lists (PD-14059). Two-column body per
 * the design review: the client block (name + wrapped street /
 * city-state-zip lines) and the Payment Method block sit wide-left while
 * Sale Date (and Refund Date, when set) stack right-aligned beside them.
 * Amount Paid is the hero — Net Amount and Processing Fee duplicated it on
 * the audit cards, so they're desktop-only. Payment Method stays a WIDE
 * block (not a right-column chip) because its empty subtype/name lines
 * drop per row, which is what kills the audit's unlabeled "- / Account
 * Funds / -" blob.
 */
export const TRANSACTION_MOBILE_CARD_CONFIG: MobileCardConfiguration = {
	cardLayout: 'zones',
	primaryFields: ['transaction-id-origin'],
	heroField: 'transaction-amounts.amount-paid-field',
	wideFields: ['client-info', 'payment-method'],
	hiddenFields: [
		'transaction-amounts.net-amount-field',
		'transaction-amounts.processing-fee-field',
	],
	// The card's one datetime is obviously the sale date — "Sale Date" ate
	// half the right column and wrapped the time (PD-14059 review note).
	labelOverrides: { 'transaction-dates.sale-date-field': 'Date' },
};

/**
 * Payment method icon value mappings
 */
export const PAYMENT_METHOD_ICON_MAPPINGS = [
	{ value: 'Credit Card', iconName: 'credit-card' },
	{ value: 'Credit Card Reader', iconName: 'credit-card' },
	{ value: 'Check', iconName: 'money-check-dollar' },
	{ value: 'Cash', iconName: 'money-bill' },
	{ value: 'ACH', iconName: 'building' },
	{ value: 'Company Credit', iconName: 'building' },
] as const;

/**
 * Transaction status value mappings
 */
export const STATUS_VALUE_MAPPINGS = [
	{
		color: { bg: '#059669', text: '#ffffff' },
		value: 'succeeded',
		displayText: 'Succeeded',
	},
	{
		color: { bg: '#059669', text: '#ffffff' },
		value: 'completed',
		displayText: 'Completed',
	},
	{
		color: { bg: '#d97706', text: '#ffffff' },
		value: 'pending',
		displayText: 'Pending',
	},
	{
		color: { bg: '#d97706', text: '#ffffff' },
		value: 'processing',
		displayText: 'Processing',
	},
	{
		color: { bg: '#dc2626', text: '#ffffff' },
		value: 'failed',
		displayText: 'Failed',
	},
	{
		color: { bg: '#dc2626', text: '#ffffff' },
		value: 'error',
		displayText: 'Error',
	},
	{
		color: { bg: '#6b7280', text: '#ffffff' },
		value: 'cancelled',
		displayText: 'Cancelled',
	},
	{
		color: { bg: '#6b7280', text: '#ffffff' },
		value: 'voided',
		displayText: 'Voided',
	},
	// The refund-transactions list exists but 'refunded' was never mapped,
	// so it rendered as an unstyled raw badge (caught in the PD-14059
	// mobile-card evidence pass). Gray = terminal/neutral, like voided.
	{
		color: { bg: '#6b7280', text: '#ffffff' },
		value: 'refunded',
		displayText: 'Refunded',
	},
] as const;

/**
 * Required fields for all transaction lists
 */
export const TRANSACTION_REQUIRED_FIELDS = [
	'transaction_summary._id',
	'transaction_summary.m_payment_externalId_erp',
	'transaction_summary.m_sm_payment_externalId',
	'transaction_summary.clientAddress',
	'transaction_summary.clientCity',
	'transaction_summary.clientName',
	'transaction_summary.clientState',
	'transaction_summary.clientZipCode',
	'transaction_summary.m_payment_datetime',
	'transaction_summary.m_payment_netAmount',
	'transaction_summary.m_payment_origin',
	'transaction_summary.m_payment_payment_method_name',
	'transaction_summary.m_payment_payment_method_type',
	'transaction_summary.m_payment_status',
	'transaction_summary.m_sm_payment_amount',
	'transaction_summary.transactionType',
	// PD-14977 — needed by the default Status column, which now separates
	// money held from money collected, and by the reconciliation lists.
	'transaction_summary.isSettled',
	'transaction_summary.orderId',
	'transaction_summary.resultText',
];

/**
 * Base filter rules for transaction lists
 */
export const BASE_FILTER_RULES: Record<string, FilterRule> = {
	transactionSalesRep: {
		defaultValue: [{ id: 'ME', label: 'Me' }],
		applyDefaultIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [
				{ type: 'rule', action: 'transactions:view:any', negated: true },
			],
		},
		lockedIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [
				{ type: 'rule', action: 'transactions:view:any', negated: true },
			],
		},
		hiddenIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [
				{ type: 'rule', action: 'transactions:view:any', negated: true },
			],
		},
	},
};
